import * as React from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import { useGlobalCoachChatState } from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';

export type CoachChatConfig = Omit<AiCoachRespondToUserMessageInput, 'message'> & { message?: undefined };

type CoachStateProviderType = {
  coachOpen: boolean;
  setCoachOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFullScreen: boolean;
  setIsFullScreen: React.Dispatch<React.SetStateAction<boolean>>;
  contextConfig: CoachChatConfig;
  setContextConfig: React.Dispatch<React.SetStateAction<CoachChatConfig>>;
  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;
  setRenderCustomMessage: React.Dispatch<
    React.SetStateAction<((props: ChatMessageProps) => React.ReactElement | null) | undefined>
  >;
  triggerElementRef: React.MutableRefObject<HTMLElement | null>;
  setTriggerElementRef: (elm: HTMLElement | null) => void;
  isContextConfigLoading?: boolean;
};
type CoachStateProviderProps = {
  children: React.ReactNode;
  coachOpen?: boolean;
  isFullScreen?: boolean;
  isContextConfigLoading?: boolean;
};

const defaultCoachState: CoachStateProviderType = {
  coachOpen: false,
  setCoachOpen: () => {},
  isFullScreen: false,
  setIsFullScreen: () => {},
  contextConfig: {
    config: {
      coachActionId: '',
      coachModeId: 'BASIC',
    },
  },
  setContextConfig: () => {},
  renderCustomMessage: undefined,
  setRenderCustomMessage: () => {},
  triggerElementRef: { current: null },
  setTriggerElementRef: () => {},
};

const CoachStateContext = createContext(defaultCoachState);

/**
 * @description This hook accesses coach state, with automatic fallback logic:
 * - First attempts to use GlobalCoachChatStateProvider if available
 * - Falls back to local CoachStateProvider if global provider is not in the tree
 * This enables gradual migration and supports both isolated and global coach state patterns.
 */
export const useCoachState = () => {
  const globalCoachStateContext = useGlobalCoachChatState();
  const localCoachStateContext = useContext(CoachStateContext);

  if (globalCoachStateContext.isContextProvided) {
    return globalCoachStateContext;
  }

  return localCoachStateContext;
};

/**
 * @description This provider is used to manage the state of Coach.
 */
const LocalCoachStateProvider = ({
  children,
  coachOpen: coachOpenProp,
  isFullScreen: isFullScreenProp,
  isContextConfigLoading,
}: CoachStateProviderProps) => {
  const [coachOpen, setCoachOpen] = useState(coachOpenProp ?? defaultCoachState.coachOpen);
  const [isFullScreen, setIsFullScreen] = useState(isFullScreenProp ?? defaultCoachState.isFullScreen);
  const [contextConfig, setContextConfig] = useState<CoachChatConfig>(defaultCoachState.contextConfig);
  const [renderCustomMessage, setRenderCustomMessage] = useState<
    ((props: ChatMessageProps) => React.ReactElement | null) | undefined
  >(defaultCoachState.renderCustomMessage);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const setTriggerElementRef = (elm: HTMLElement | null) => {
    triggerElementRef.current = elm;
  };

  return (
    <CoachStateContext.Provider
      value={{
        coachOpen,
        setCoachOpen,
        isFullScreen,
        setIsFullScreen,
        isContextConfigLoading,
        contextConfig,
        setContextConfig: (...data) => {
          setContextConfig(...data);
        },
        renderCustomMessage,
        setRenderCustomMessage,
        triggerElementRef,
        setTriggerElementRef,
      }}
    >
      {children}
    </CoachStateContext.Provider>
  );
};

export const CoachStateProvider = ({
  children,
  coachOpen,
  isFullScreen,
  isContextConfigLoading,
}: CoachStateProviderProps) => {
  const { isContextProvided, setCoachOpen, setIsFullScreen, setContextConfig, setIsContextConfigLoading } =
    useGlobalCoachChatState();

  useEffect(() => {
    if (!isContextProvided) return;
    if (coachOpen) setCoachOpen(true);
  }, [isContextProvided, coachOpen, setCoachOpen, setContextConfig]);

  useEffect(() => {
    if (!isContextProvided) return;
    setIsFullScreen(isFullScreen ?? false);
  }, [isContextProvided, isFullScreen, setIsFullScreen]);

  useEffect(() => {
    if (!isContextProvided) return;
    setIsContextConfigLoading(isContextConfigLoading ?? false);
  }, [isContextProvided, isContextConfigLoading, setIsContextConfigLoading]);

  if (isContextProvided) {
    return <>{children}</>;
  }

  return (
    <LocalCoachStateProvider
      coachOpen={coachOpen}
      isFullScreen={isFullScreen}
      isContextConfigLoading={isContextConfigLoading}
    >
      {children}
    </LocalCoachStateProvider>
  );
};
